import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  BottomNavigation,
  BottomNavigationAction,
  Avatar,
  Divider,
  Button,
  Paper,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import BusinessIcon from "@mui/icons-material/Business";
import EventIcon from "@mui/icons-material/Event";
import MessageIcon from "@mui/icons-material/Message";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import LocationPrompt from "./LocationPrompt";
import { NAVY, CRIMSON, GOLD, PAGE_BG_GRADIENT } from "../theme/theme";
import logo from "url:../../assets/logo.png";
import { alpha } from "@mui/material/styles";

// Nav items are defined inside the component so labels can use t()

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const isGuest = !user;

  // Messages is a private inbox — hide it from guests (the route also enforces auth).
  const NAV_ITEMS = [
    { label: t("common.home"), path: "/app/feed", icon: <HomeIcon /> },
    { label: t("common.business"), path: "/app/company", icon: <BusinessIcon /> },
    { label: t("common.events"), path: "/app/events", icon: <EventIcon /> },
    ...(isGuest ? [] : [{ label: t("common.messages"), path: "/app/messages", icon: <MessageIcon /> }]),
  ];

  const BOTTOM_NAV_ITEMS = [
    { label: t("common.home"), path: "/app/feed", icon: <HomeIcon /> },
    { label: t("common.business"), path: "/app/company", icon: <BusinessIcon /> },
    { label: t("common.events"), path: "/app/events", icon: <EventIcon /> },
    ...(isGuest ? [] : [{ label: t("common.messages"), path: "/app/messages", icon: <MessageIcon /> }]),
  ];

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const currentBottomNav = BOTTOM_NAV_ITEMS.findIndex(
    (item) => location.pathname.startsWith(item.path)
  );

  const handleBottomNavChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(BOTTOM_NAV_ITEMS[newValue].path);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Top AppBar — deep navy */}
      <AppBar position="sticky" elevation={0}
        sx={{
          bgcolor: NAVY,
          borderBottom: `1px solid ${alpha("#fff", 0.08)}`,
          boxShadow: `0 2px 16px ${alpha(NAVY, 0.4)}`,
        }}
      >
        <Toolbar sx={{ gap: 1, minHeight: 56 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setDrawerOpen(true)}
            aria-label="menu"
            sx={{ color: "rgba(255,255,255,0.9)" }}
          >
            <MenuIcon />
          </IconButton>

          {/* Brand logo */}
          <Box
            sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
            onClick={() => navigate("/app/feed")}
          >
            <Box
              component="img"
              src={logo}
              alt="Sajha Social"
              sx={{ height: 40, width: "auto", mixBlendMode: "screen" }}
            />
          </Box>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Search icon button */}
          <IconButton
            onClick={() => navigate("/app/search")}
            aria-label="search"
            sx={{
              color: "rgba(255,255,255,0.85)",
              "&:hover": { color: "white", bgcolor: alpha("#fff", 0.08) },
            }}
          >
            <SearchIcon />
          </IconButton>

          {/* Language toggle */}
          <Button
            size="small"
            variant="text"
            onClick={() => setLanguage(language === "ne" ? "en" : "ne")}
            sx={{
              color: alpha("#fff", 0.85),
              fontSize: "0.75rem",
              fontWeight: 600,
              textTransform: "none",
              px: 1,
              minWidth: 0,
              "&:hover": { color: "white", bgcolor: alpha("#fff", 0.08) },
            }}
          >
            {language === "ne" ? "English" : "नेपाली"}
          </Button>

          {isGuest && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate("/login")}
              sx={{
                color: "white",
                borderColor: alpha("#fff", 0.5),
                fontSize: "0.75rem",
                fontWeight: 700,
                textTransform: "none",
                px: 1.5,
                "&:hover": { borderColor: "white", bgcolor: alpha("#fff", 0.1) },
              }}
            >
              {t("landing.login")}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* Left Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { borderRadius: "0 20px 20px 0", overflow: "hidden" } }}
      >
        <Box sx={{ width: 280, display: "flex", flexDirection: "column", height: "100%", backgroundImage: PAGE_BG_GRADIENT }}>
          {/* Drawer Header — rich gradient */}
          <Box
            sx={{
              p: 2.5,
              background: `linear-gradient(160deg, ${NAVY} 0%, #1a3a7a 60%, ${alpha(CRIMSON, 0.85)} 100%)`,
              color: "white",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: -30,
                right: -30,
                width: 100,
                height: 100,
                borderRadius: "50%",
                background: alpha(GOLD, 0.15),
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -20,
                left: 20,
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: alpha("#fff", 0.05),
              },
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative", zIndex: 1 }}>
              <Box>
                <Avatar
                  sx={{
                    width: 52,
                    height: 52,
                    bgcolor: alpha(GOLD, 0.9),
                    color: NAVY,
                    fontSize: "1.2rem",
                    fontWeight: 800,
                    mb: 1.5,
                    boxShadow: `0 4px 12px ${alpha("#000", 0.3)}`,
                  }}
                >
                  {user ? user.first_name.charAt(0).toUpperCase() : "?"}
                </Avatar>
                <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                  {user ? `${user.first_name} ${user.last_name}` : "Guest"}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 500 }}>
                  {user ? `@${user.username}` : ""}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => setDrawerOpen(false)}
                sx={{ color: "rgba(255,255,255,0.7)", mt: -0.5, "&:hover": { color: "white" } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Nav Links */}
          <List sx={{ flex: 1, py: 1.5, px: 1 }}>
            {NAV_ITEMS.map((item) => {
              const isSelected = location.pathname.startsWith(item.path);
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    selected={isSelected}
                    sx={{
                      borderRadius: 2,
                      py: 1,
                      "&.Mui-selected": {
                        bgcolor: alpha(CRIMSON, 0.08),
                        color: CRIMSON,
                        "& .MuiListItemIcon-root": { color: CRIMSON },
                        "&:hover": { bgcolor: alpha(CRIMSON, 0.12) },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 38, color: isSelected ? CRIMSON : "#6B7280" }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: isSelected ? 700 : 500, fontSize: "0.92rem" }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}

            <Divider sx={{ my: 1.5 }} />

            {user && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to={`/app/profile/${user.username}`}
                  selected={location.pathname.startsWith("/app/profile")}
                  sx={{ borderRadius: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 38, color: "#6B7280" }}>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("common.profile")}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: "0.92rem" }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </List>

          {/* Language toggle + Logout */}
          <Divider />
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setLanguage(language === "ne" ? "en" : "ne")}
              fullWidth
              sx={{ borderColor: alpha(NAVY, 0.3), color: NAVY, textTransform: "none", fontWeight: 600, "&:hover": { borderColor: NAVY, bgcolor: alpha(NAVY, 0.05) } }}
            >
              {language === "ne" ? "English" : "नेपाली"}
            </Button>
            {isGuest ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonIcon />}
                onClick={() => navigate("/login")}
                fullWidth
              >
                {t("landing.login")}
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                fullWidth
              >
                {t("common.logout")}
              </Button>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Page content */}
      <Box
        component="main"
        sx={{
          flex: 1,
          pb: "58px", // bottom nav height
          backgroundImage: PAGE_BG_GRADIENT,
          backgroundAttachment: "fixed",
          backgroundColor: "background.default",
        }}
      >
        <Outlet key={refreshKey} />
      </Box>

      {/* Bottom Navigation — matches the gradient page headers */}
      <Paper
        elevation={0}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          borderRadius: 0,
          background: `linear-gradient(135deg, ${NAVY} 0%, #1a3a7a 55%, ${alpha(CRIMSON, 0.92)} 100%)`,
          boxShadow: `0 -4px 16px ${alpha(NAVY, 0.3)}`,
        }}
      >
        <BottomNavigation
          value={currentBottomNav >= 0 ? currentBottomNav : false}
          onChange={handleBottomNavChange}
          showLabels
          sx={{ height: 58 }}
        >
          {BOTTOM_NAV_ITEMS.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={item.icon}
              sx={{
                minWidth: 0,
                px: 0,
                "& .MuiBottomNavigationAction-label": { fontSize: "0.63rem", mt: 0.3 },
              }}
            />
          ))}
        </BottomNavigation>
      </Paper>

      {/* Manual location prompt — shown when GPS + IP geo both failed */}
      <LocationPrompt />
    </Box>
  );
}
